import _ from "lodash"
import React, { useContext, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSubmitAnswersMutation, usePaperSheetQuery, usePaperQuery } from "../../generated-types"
import { IntroPage } from "./Intro"
import { FMatAnswerSeqPage } from "./FMatAnswerSeq"
import { FMatAnswerOnePage } from "./FMatAnswerOne"
import FMatAnswerHmiPage from "./FMatAnswerHmi"
import { useNavigate, useParams } from "react-router-dom"
import { LanguageContext } from "../../components/context/LanguageContext"
import { EmptyPage } from "./EmptyPage"
import { Spinning } from "grommet-controls"
import { Box, Button, Text } from "grommet"
import { useEffect } from "react"
import { handleGqlError, resolveApolloError } from "../../components/utils"
import { ToastsStore } from "react-toasts"

const TRANSITION_TIME_MS = 280

// 진행 답안(sheet)과 현재 위치(idx)를 브라우저 localStorage에 저장해
// 새로고침/앱 리로드 시에도 이어서 응시할 수 있게 한다.
const PROGRESS_VERSION = 1
const progressKey = (paperId: string) => `checkdino:diag:${paperId}`

const loadProgress = (paperId?: string): { idx: number; sheet: any[] } | null => {
  if (!paperId) return null
  try {
    const raw = window.localStorage.getItem(progressKey(paperId))
    if (!raw) return null
    const data = JSON.parse(raw)
    if (data.v !== PROGRESS_VERSION || !Array.isArray(data.sheet)) return null
    // JSON 직렬화로 sheet의 빈 슬롯이 null이 되므로 undefined로 되돌린다.
    const sheet = data.sheet.map((s) => (s == null ? undefined : s))
    return { idx: typeof data.idx === "number" ? data.idx : 0, sheet }
  } catch {
    return null
  }
}

const saveProgress = (paperId: string, idx: number, sheet: any[]) => {
  try {
    window.localStorage.setItem(
      progressKey(paperId),
      JSON.stringify({ v: PROGRESS_VERSION, idx, sheet, savedAt: Date.now() })
    )
  } catch {
    // 저장 실패(용량 초과 등)는 무시 — 응시 흐름을 막지 않는다.
  }
}

const clearProgress = (paperId?: string) => {
  if (!paperId) return
  try {
    window.localStorage.removeItem(progressKey(paperId))
  } catch {
    // ignore
  }
}

type Action =
  | { type: "prev" }
  | { type: "next" }
  | { type: "advance" }
  | { type: "update"; score: number[]; done: boolean; finished: boolean }
  | { type: "goto"; idx: number }
  | { type: "magic"; key: string }

const REDUCER = (state, action: Action) => {
  let toIdx = state.idx

  switch (action.type) {
    case "prev":
      toIdx = state.idx === 0 ? toIdx : toIdx - 1
      return { ...state, idx: toIdx, waiting: false, score: state.sheet[toIdx] }
    case "next":
      if (state.finished) return state
      toIdx = toIdx + 1
      return { ...state, idx: toIdx, waiting: false, score: state.sheet[toIdx] }
    case "advance":
      if (state.finished) return state
      toIdx = toIdx + 1
      return { ...state, idx: toIdx, waiting: false, done: false, score: state.sheet[toIdx] }
    case "goto":
      return { ...state, idx: action.idx, waiting: false, done: false, score: state.sheet[action.idx] }
    case "update":
      const newState = { ...state, score: action.score, waiting: action.done, finished: action.finished || false }
      if (action.done) {
        const newSheet = [...state.sheet]
        newSheet[state.idx] = action.score
        newState.sheet = newSheet
        newState.done = true
      }
      return newState
    case "magic":
      return action.key === "A"
        ? {
            idx: 16,
            score: undefined,
            waiting: false,
            finished: false,
            sheet: [undefined, ..._.times(15, () => _.shuffle([4, 3, 2, 1]))],
          }
        : {
            idx: 27,
            score: undefined,
            waiting: false,
            finished: false,
            sheet: [...state.sheet, undefined, ..._.times(10, () => [1])],
          }
  }
}

// 유저의 행동에 따른 값 및 상태값 제어 그리고 페이지 페칭 전담
// 결과 페이지만 별도의 URL로 구성
// 저장소에 처리중이었던 진단 내용이 있으면 resume 가능
export const FMatControlPage: React.FC = (props) => {
  const { paperId } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  let { lang } = useContext(LanguageContext)
  if (!["en", "ko"].includes(lang)) lang = "en"
  const { data: paperData, error: paperError } = usePaperQuery({
    variables: { paperId },
  })

  useEffect(() => {
    if (paperError) {
      handleGqlError(paperError)
      navigate("/")
    }
  }, [paperError])
  const { loading, data, error } = usePaperSheetQuery({ variables: { paperId, lang }, fetchPolicy: "no-cache" })

  const [submitAnswer] = useSubmitAnswersMutation()
  const [state, dispatch] = React.useReducer(REDUCER, undefined, () => {
    // localStorage에 진행 내용이 있으면 이어서 응시(resume)
    const saved = loadProgress(paperId)
    return {
      idx: saved ? saved.idx : 0,
      score: saved ? saved.sheet[saved.idx] : undefined, // 현재 표시되는 점수
      done: false,
      waiting: false,
      finished: false,
      sheet: saved ? saved.sheet : [], // 점수 입력 내용
    }
  })

  useEffect(() => {
    if (!paperData) return

    if (/^5LL/.test(paperData.paper.item.id)) navigate(`/papers/${paperId}`)
    if (paperData.paper.status === "DONE") {
      clearProgress(paperId) // 이미 완료된 응시면 잔존 진행 내용 정리
      navigate(`/paper/${paperId}/result`)
    }
  }, [paperData])

  const { idx, score, waiting, sheet } = state
  const steps = data ? data.paperSheet.steps : []

  const seq = useMemo(
    () =>
      _.chain(data ? data.paperSheet.steps : [])
        .map((v, idx) => ({ ...v, idx }))
        .groupBy((v) => v.type + "/" + v.group)
        .mapValues((v) => v.map(({ idx }) => idx))
        .value(),
    [data]
  )

  const onUpdateScore = React.useCallback(
    (score: number[], done: boolean) => {
      dispatch({ type: "update", score, done, finished: done && state.idx === steps.length - 1 })
    },
    [state, steps]
  )

  useEffect(() => {
    let timer

    if (state.done && !state.finished) {
      timer = setTimeout(() => dispatch({ type: "advance" }), TRANSITION_TIME_MS)
    }
    return () => timer && clearTimeout(timer)
  }, [state])

  // 진행 내용이 바뀔 때마다 localStorage에 저장(완료 전까지)
  useEffect(() => {
    if (!paperId || state.finished) return
    if (state.sheet.length > 0 || state.idx > 0) {
      saveProgress(paperId, state.idx, state.sheet)
    }
  }, [state.idx, state.sheet, state.finished, paperId])

  // 복원한 위치가 현재 진단 문항 수를 벗어나면(진단 구성 변경 등) 처음으로 복구
  useEffect(() => {
    if (steps.length > 0 && state.idx >= steps.length) {
      clearProgress(paperId)
      dispatch({ type: "goto", idx: 0 })
    }
  }, [steps.length, state.idx, paperId])

  // Submitting the answers can fail (network drop, expired login). Without
  // feedback the test taker is left tapping a last question that never moves.
  const [submitError, setSubmitError] = useState<"auth" | "other" | null>(null)
  const [submitAttempt, setSubmitAttempt] = useState(0)
  const submittingRef = useRef(false)

  useEffect(() => {
    if (state.finished && !submittingRef.current) {
      submittingRef.current = true
      setSubmitError(null)
      const answerSheet = _.chain(state.sheet)
        .map((seq, i) =>
          seq
            ? _.chain(seq)
                .map((a, ai) => [ai + 1, a])
                .filter(([, s]) => s > 0)
                .orderBy([([, a]) => a], ["desc"])
                .map(([ai]) => steps[i].res[ai].id)
                .join(",")
                .value()
            : undefined
        )
        .compact()
        .value()

      submitAnswer({ variables: { paperId, answers: answerSheet } })
        .then((r) => {
          clearProgress(paperId) // 제출 완료 → 저장된 진행 내용 제거
          navigate(`/paper/${paperId}/result`)
        })
        .catch((e) => {
          console.error(e)
          // An earlier attempt reached the server even though its reply was lost.
          if ((e?.graphQLErrors || []).some((g) => g?.extensions?.code === "ALREADY_DONE")) {
            clearProgress(paperId)
            navigate(`/paper/${paperId}/result`)
            return
          }
          const isAuth =
            e?.networkError?.statusCode === 401 ||
            (e?.graphQLErrors || []).some((g) => g?.extensions?.code === "UNAUTHENTICATED")
          setSubmitError(isAuth ? "auth" : "other")
        })
        .finally(() => {
          submittingRef.current = false
        })
    }
  }, [state, submitAttempt, paperId, steps, navigate, submitAnswer])

  if (submitError) {
    return (
      <EmptyPage>
        <Box gap="medium" align="center">
          <Text color="status-error" textAlign="center">
            {submitError === "auth" ? t("error.no-auth") : t("error.submit-failed")}
          </Text>
          {submitError === "auth" ? (
            <Button primary label={t("button.confirm")} onClick={() => navigate("/")} />
          ) : (
            <Button primary label={t("button.retry")} onClick={() => setSubmitAttempt((n) => n + 1)} />
          )}
        </Box>
      </EmptyPage>
    )
  }

  if (loading) {
    return (
      <EmptyPage>
        <Spinning size="medium" kind="circle" />
      </EmptyPage>
    )
  }

  if (error) {
    const errorCodes = resolveApolloError(error)
    // const { networkError, graphQLErrors } = error
    // const errorCodes = []
    // if (networkError) errorCodes.push(networkError)
    // graphQLErrors.forEach(({ message }) => {
    //   errorCodes.push(message)
    // })
    return (
      <EmptyPage>
        {errorCodes.map((code, i) => (
          <Text key={i} color="status-error">
            {t(`error.${code}`)}
          </Text>
        ))}
      </EmptyPage>
    )
  }

  const { type, group, res } = steps[idx]
  const key = type + "/" + group

  if (type === "INFO") {
    const { desc } = res[0]

    return (
      <IntroPage
        onRequestNext={() => dispatch({ type: "next" })}
        description={desc || ""}
        title={`${paperData.paper.item.name}${group.startsWith("_") ? "" : " (Part " + group + ")"}`}
        onMagic={(key) => {
          dispatch({ type: "magic", key })
        }}
        id={group}
      />
    )
  } else {
    const props = {
      index: seq[key].indexOf(idx) + 1,
      total: seq[key].length,
      // onDone: onNext,
      score,
      waiting,
      hasPrevious: seq[key].includes(idx - 1),
      hasNext: !!sheet[idx],
      onUpdateScore,
      onPrevious: () => dispatch({ type: "prev" }),
      onNext: () => dispatch({ type: "next" }),
    }

    const [q, ...a] = res
    switch (type) {
      case "FMAT_SEQ":
        return <FMatAnswerSeqPage {...props} question={q.desc} answers={a.map(({ desc }) => desc)} />
      case "FMAT_ONE":
        return <FMatAnswerOnePage {...props} question={q.desc} answers={a.map(({ desc }) => desc)} />
      case "FMAT_HMI":
        return <FMatAnswerHmiPage {...props} question={undefined} answers={[res[0].desc, res[1].desc]} />
    }
  }

  return <EmptyPage />
}

export const DiagRedirector = (props) => {
  const { paperId } = useParams()
  const navigate = useNavigate()
  const { data, called, error: redirectError } = usePaperQuery({
    variables: { paperId },
  })

  useEffect(() => {
    if (redirectError) {
      handleGqlError(redirectError)
      navigate("/")
    }
  }, [redirectError])

  useEffect(() => {
    if (!data) {
      if (called) {
        ToastsStore.error("Wrong Access")
        navigate("/")
        return
      }
    }

    if (data.paper.status === "DONE") {
      navigate(`/paper/${paperId}/result`)
      return
    }

    navigate(`/paper/${paperId}/diag`)
  }, [data])
  return null
}
